import { FC } from "react";
import dayjs from "dayjs";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import Heading from "@/components/atoms/Heading";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import { Company, Contract, ContractLight } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import {
  contractsUIConfig,
  getContractCardDetails,
  getContractTradeType,
  getContractProgressPercent,
} from "@/utils/Contracts";
import Progress from "@/components/atoms/Progress";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import { useCreateOrderFromContractStore } from "@/stores/createOrderFromContractStore";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchContractById } from "@/services/Contracts";
import { useDebouncedPrefetch } from "@/hooks/useDebouncedPrefetch";

interface Props {
  contract: ContractLight;
  approveContract: () => void;
  isPendingMutateApproval: boolean;
}

const ContractCard: FC<Props> = ({ contract, approveContract, isPendingMutateApproval }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const schedulePrefetch = useDebouncedPrefetch();
  const { currentCompany, isViewer } = useAuth();
  const contractTradeType = getContractTradeType(contract, currentCompany);
  const { iconName, companyIcon, partnerIcon } = contractsUIConfig(contractTradeType);
  const { sellerName, buyerName, createdDate, notApproved } = getContractCardDetails(contract);
  const open = useCreateOrderFromContractStore((e) => e.open);

  const handleApproveContract = async () => {
    approveContract();
  };

  return (
    <div className={styles.contractCardRoot}>
      <SVGAsset name={iconName} width="36" height="36" className={styles.icon} />
      <Text size="xs" className={styles.dateCreated} color="secondary">
        {createdDate && dayjs(createdDate).local().format("DD.MM.YYYY")}
      </Text>
      <Stack gap="17px" flex={1}>
        <Stack gap="2px">
          <Text span={true} size="xs" tt="uppercase" color="tertiary">
            {contract.id} {contract.name}
          </Text>
          <Heading order={4} styleOrder={5} strong={true}>
            {contract.contractProduct.companySpecificProduct.companySpecificProductName}
          </Heading>
        </Stack>
        <Stack gap={0} className={styles[contractTradeType]}>
          <Text size="md" strong={true}>
            <SVGAsset name={companyIcon} width="12" height="12" className={styles.tradeIcon} />
            {contractTradeType === ContractTradeTypeEnum.SELL ? sellerName : buyerName}
          </Text>
          <Text size="md" strong={true}>
            <SVGAsset name={partnerIcon} width="12" height="12" className={styles.tradeIcon} />
            {contractTradeType === ContractTradeTypeEnum.SELL ? buyerName : sellerName}
          </Text>
        </Stack>
        {contract.contractStatus !== ContractStatusEnum.PENDING_APPROVAL ? (
          <>
            <Progress value={getContractProgressPercent(contract)} contractTradeType={contractTradeType} />
          </>
        ) : (
          <ApprovalWarning notApproved={notApproved} contract={contract} />
        )}
        <Group justify="space-between">
          <Group>
            <CTA
              intent="primary"
              size="sm"
              onClick={() => navigate(`/contracts/details/${contract.id}`)}
              onMouseEnter={() => schedulePrefetch(() => prefetchContractById(queryClient, contract.id))}
            >
              {t("common:details")}
            </CTA>
            {contract.contractStatus === ContractStatusEnum.PENDING_APPROVAL && (
              <CTA
                intent="primary"
                size="sm"
                onClick={handleApproveContract}
                loading={isPendingMutateApproval}
                disabled={
                  !!contract.approvals.find((approval) => approval.approvalCompany === currentCompany?.companyId) ||
                  isViewer(currentCompany)
                }
              >
                {t("contracts:approve")}
              </CTA>
            )}
            {contract.contractStatus === ContractStatusEnum.ACTIVE && (
              <CTA
                intent="primary"
                size="sm"
                onClick={() => open(contract, "contractLight")}
                disabled={isViewer(currentCompany)}
              >
                {t("orders:newOrder")}
              </CTA>
            )}
          </Group>
        </Group>
      </Stack>
    </div>
  );
};

interface ApprovalWarningProps {
  notApproved: Company[];
  contract: Contract | ContractLight;
}

export const ApprovalWarning: FC<ApprovalWarningProps> = ({ notApproved, contract }) => (
  <p style={{ position: "relative", margin: 0 }}>
    <SVGAsset name="yellow-exclamation" className={styles.approvalWarning} />
    <ApprovalWarningText notApproved={notApproved} contract={contract} />
  </p>
);

interface ApprovalWarningTextProps {
  notApproved: Company[];
  contract: Contract | ContractLight;
}

export const ApprovalWarningText: FC<ApprovalWarningTextProps> = ({ notApproved }) => {
  const { t } = useTranslation();
  let warningText = "";

  if (notApproved.length > 1) {
    warningText = `${t("contracts:contractApproveWarning")}`;
  } else if (notApproved.length === 1) {
    warningText = `${notApproved[0].companyName} ${t("contracts:needToApproveContract")}`;
  }

  return (
    <Text span={true} color="warning" size="xs">
      {warningText}
    </Text>
  );
};

export default ContractCard;
